import { useMemo, useState } from "react";
import styled from "@emotion/styled";
import Lowlight from "react-lowlight";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { Uuid } from "@/domains/global/identifiers";
import { toastModule } from "@/modules/toast";
import {
  AudioRecordingInternalResponseSchema,
  fetchAudioRecordingInternal,
  fetchAudioRecordingRecallData,
  getAudioRecordingDownloadUrl,
  RecallDataInternalResponseSchema,
} from "@/pages/audio-recordings/shared/api";
import { AudioRecordingEventTimeline } from "@/pages/audio-recordings/view/audio-recording-event-timeline";
import {
  StatusBadge,
  AudioPlayerSection,
  TranscriptSection,
  RelatedEntitiesSection,
} from "@/pages/audio-recordings/view/components";

const DEFAULT_GCP_PROJECT = "mem-gcp-production";
const DEFAULT_BUCKET_NAME = "mem-private-uploads";

const Container = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #111827;
`;

const RecordingId = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  font-family: "Berkeley Mono Variable", monospace;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  padding: 2px 6px;
  cursor: pointer;
  color: #64748b;
  border-radius: 4px;

  &:hover {
    background: #f1f5f9;
    color: #374151;
  }
`;

const TimestampRow = styled.div`
  display: flex;
  gap: 24px;
  font-size: 12px;
  color: #64748b;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const StatusOverview = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
`;

const StatusLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CollapsibleSection = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
`;

const CollapsibleHeader = styled.button<{ isOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: ${({ isOpen }) => (isOpen ? "#f8fafc" : "#fff")};
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-align: left;

  &:hover {
    background: #f8fafc;
  }
`;

const CollapsibleContent = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
`;

const JsonBlock = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  font-size: 12px;
  max-height: 400px;
  overflow-y: auto;
`;

const TrashedBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  color: #991b1b;
  font-size: 13px;
  font-weight: 500;
`;

const RecallDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const RecallDataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RecallDataLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
`;

const RecallDataValue = styled.span`
  font-size: 13px;
  color: #111827;
  font-family: "Berkeley Mono Variable", monospace;
`;

const RecallDownloadLinks = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const RecallErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  color: #991b1b;
  font-size: 13px;
`;

export const AudioRecordingsViewPage = () => {
  const navigate = useNavigate();
  const { audioRecordingId } = useParams<{ audioRecordingId: Uuid }>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallData, setRecallData] = useState<RecallDataInternalResponseSchema | null>(null);
  const [recallLoading, setRecallLoading] = useState(false);
  const [recallFetched, setRecallFetched] = useState(false);

  const query = useQuery({
    queryKey: ["audio-recording-internal", audioRecordingId],
    queryFn: () => (audioRecordingId ? fetchAudioRecordingInternal(audioRecordingId) : null),
    enabled: Boolean(audioRecordingId),
    staleTime: 30 * 1000,
  });

  const recording: AudioRecordingInternalResponseSchema | null = useMemo(
    () => query.data ?? null,
    [query.data]
  );

  const gcsConsoleUrl = useMemo(() => {
    if (!recording || !audioRecordingId) return "";
    return `https://console.cloud.google.com/storage/browser/${DEFAULT_BUCKET_NAME}/${recording.owned_by_space_account_id}/${audioRecordingId}?project=${DEFAULT_GCP_PROJECT}`;
  }, [recording, audioRecordingId]);

  const handleDownload = async () => {
    if (!audioRecordingId) return;
    setIsDownloading(true);
    try {
      const response = await getAudioRecordingDownloadUrl(audioRecordingId);
      if (response?.download_url) {
        window.open(response.download_url, "_blank");
      } else {
        toastModule.triggerToast({ content: "Failed to get download URL" });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to download";
      toastModule.triggerToast({ content: message });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyId = () => {
    if (audioRecordingId) {
      navigator.clipboard.writeText(audioRecordingId);
      toastModule.triggerToast({ content: "ID copied to clipboard" });
    }
  };

  const handleRecallToggle = async () => {
    const newOpen = !recallOpen;
    setRecallOpen(newOpen);

    // Fetch recall data when opening for the first time
    if (newOpen && !recallFetched && audioRecordingId) {
      setRecallLoading(true);
      try {
        const data = await fetchAudioRecordingRecallData(audioRecordingId);
        setRecallData(data);
        setRecallFetched(true);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to fetch Recall data";
        toastModule.triggerToast({ content: message });
      } finally {
        setRecallLoading(false);
      }
    }
  };

  const handleRefreshRecallData = async () => {
    if (!audioRecordingId) return;
    setRecallLoading(true);
    try {
      const data = await fetchAudioRecordingRecallData(audioRecordingId);
      setRecallData(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch Recall data";
      toastModule.triggerToast({ content: message });
    } finally {
      setRecallLoading(false);
    }
  };

  if (!audioRecordingId) {
    return <Container>Missing audio recording ID.</Container>;
  }

  if (query.isLoading) {
    return <Container>Loading...</Container>;
  }

  if (query.isError || !recording) {
    return (
      <Container>
        Failed to load audio recording.
        <ButtonRow>
          <Button type="button" onClick={() => query.refetch()}>
            Retry
          </Button>
        </ButtonRow>
      </Container>
    );
  }

  const capturedAudio = recording.captured_audio;
  const title = (recording.value_json as Record<string, unknown>)?.title as string | undefined;

  return (
    <Container>
      {/* Header */}
      <Header>
        <TitleRow>
          <TitleSection>
            <Title>{title ?? "Untitled Recording"}</Title>
            <RecordingId>
              {audioRecordingId}
              <CopyButton onClick={handleCopyId} title="Copy ID">
                📋
              </CopyButton>
            </RecordingId>
          </TitleSection>
        </TitleRow>

        <TimestampRow>
          <span>Created: {new Date(recording.locally_created_at).toLocaleString()}</span>
          <span>Modified: {new Date(recording.locally_modified_at).toLocaleString()}</span>
        </TimestampRow>

        <ButtonRow>
          <Button type="button" variant="soft" onClick={() => query.refetch()}>
            Refresh
          </Button>
          {recording.associated_note_id && (
            <Button
              type="button"
              variant="soft"
              onClick={() =>
                navigate(
                  atlasRoutes.notesView({ params: { noteId: recording.associated_note_id! } }).path
                )
              }
            >
              View Note
            </Button>
          )}
          <Button type="button" variant="soft" onClick={() => window.open(gcsConsoleUrl, "_blank")}>
            View in GCS
          </Button>
          <Button type="button" variant="soft" onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? "Downloading..." : "Download"}
          </Button>
        </ButtonRow>
      </Header>

      {/* Trashed Banner */}
      {recording.trashed_at && (
        <TrashedBanner>
          This recording was trashed on {new Date(recording.trashed_at).toLocaleString()}
        </TrashedBanner>
      )}

      {/* Status Overview */}
      <StatusOverview>
        <StatusItem>
          <StatusLabel>Recording Status</StatusLabel>
          <StatusBadge status={recording.recording_status} />
        </StatusItem>
        <StatusItem>
          <StatusLabel>Upload Status</StatusLabel>
          <StatusBadge status={recording.upload_status} />
        </StatusItem>
        <StatusItem>
          <StatusLabel>Processing Status</StatusLabel>
          <StatusBadge status={recording.processing_status} />
        </StatusItem>
      </StatusOverview>

      {/* Two Column: Audio Player + Related Entities */}
      <TwoColumnGrid>
        <AudioPlayerSection
          capturedAudioId={recording.captured_audio_id}
          hasFile={capturedAudio?.has_file ?? false}
          hasMp3={capturedAudio?.has_mp3 ?? false}
          durationSeconds={capturedAudio?.audio_duration_seconds ?? null}
          mimeType={capturedAudio?.mime_type ?? ""}
          filename={capturedAudio?.filename ?? ""}
        />
        <RelatedEntitiesSection
          spaceAccountId={recording.owned_by_space_account_id}
          capturedAudioId={recording.captured_audio_id}
          associatedNoteId={recording.associated_note_id ?? null}
          associatedSessionId={
            ((capturedAudio as Record<string, unknown>)?.associated_session_id as string) ?? null
          }
          associatedClientId={recording.associated_client_id ?? null}
        />
      </TwoColumnGrid>

      {/* Transcript Section */}
      <TranscriptSection
        transcriptText={
          ((capturedAudio as Record<string, unknown>)?.transcript_text as string) ?? null
        }
        transcriptArray={
          ((capturedAudio as Record<string, unknown>)?.transcript_array as {
            sort_key: string;
            transcript: string;
            channel: number;
            disconnected?: boolean;
          }[]) ?? null
        }
        transcriptProvider={capturedAudio?.transcript_provider ?? null}
        transcribedAt={capturedAudio?.transcribed_at ?? null}
      />

      {/* Event Timeline */}
      <AudioRecordingEventTimeline
        audioRecordingId={audioRecordingId}
        createdAt={recording.locally_created_at}
      />

      {/* Recall AI Data Section */}
      <CollapsibleSection>
        <CollapsibleHeader isOpen={recallOpen} onClick={handleRecallToggle}>
          <span>Recall AI Data</span>
          <span>{recallOpen ? "▼" : "▶"}</span>
        </CollapsibleHeader>
        <CollapsibleContent isOpen={recallOpen}>
          {recallLoading ? (
            <div>Loading Recall data...</div>
          ) : recallData?.error_message && !recallData?.recall_sdk_upload_id ? (
            <RecallErrorMessage>{recallData.error_message}</RecallErrorMessage>
          ) : recallData ? (
            <>
              <RecallDataGrid>
                <RecallDataItem>
                  <RecallDataLabel>SDK Upload ID</RecallDataLabel>
                  <RecallDataValue>{recallData.recall_sdk_upload_id ?? "N/A"}</RecallDataValue>
                </RecallDataItem>
                <RecallDataItem>
                  <RecallDataLabel>SDK Upload Status</RecallDataLabel>
                  <RecallDataValue>{recallData.sdk_upload_status ?? "N/A"}</RecallDataValue>
                </RecallDataItem>
                <RecallDataItem>
                  <RecallDataLabel>Recording ID</RecallDataLabel>
                  <RecallDataValue>{recallData.recording_id ?? "N/A"}</RecallDataValue>
                </RecallDataItem>
                <RecallDataItem>
                  <RecallDataLabel>Recording Status</RecallDataLabel>
                  <RecallDataValue>{recallData.recording_status ?? "N/A"}</RecallDataValue>
                </RecallDataItem>
                <RecallDataItem>
                  <RecallDataLabel>Duration</RecallDataLabel>
                  <RecallDataValue>
                    {recallData.recording_duration_seconds != null
                      ? `${Math.floor(recallData.recording_duration_seconds / 60)}:${String(
                          Math.floor(recallData.recording_duration_seconds % 60)
                        ).padStart(2, "0")}`
                      : "N/A"}
                  </RecallDataValue>
                </RecallDataItem>
                <RecallDataItem>
                  <RecallDataLabel>Completed At</RecallDataLabel>
                  <RecallDataValue>
                    {recallData.sdk_upload_completed_at
                      ? new Date(recallData.sdk_upload_completed_at).toLocaleString()
                      : "N/A"}
                  </RecallDataValue>
                </RecallDataItem>
              </RecallDataGrid>

              {recallData.error_message && (
                <RecallErrorMessage style={{ marginBottom: 16 }}>
                  {recallData.error_message}
                </RecallErrorMessage>
              )}

              {recallData.media_shortcuts && (
                <RecallDownloadLinks>
                  {recallData.media_shortcuts.audio_mixed?.download_url && (
                    <Button
                      type="button"
                      variant="soft"
                      onClick={() =>
                        window.open(
                          recallData.media_shortcuts!.audio_mixed!.download_url!,
                          "_blank"
                        )
                      }
                    >
                      Download Audio (Recall)
                    </Button>
                  )}
                  {recallData.media_shortcuts.video_mixed?.download_url && (
                    <Button
                      type="button"
                      variant="soft"
                      onClick={() =>
                        window.open(
                          recallData.media_shortcuts!.video_mixed!.download_url!,
                          "_blank"
                        )
                      }
                    >
                      Download Video (Recall)
                    </Button>
                  )}
                  {recallData.media_shortcuts.transcript?.download_url && (
                    <Button
                      type="button"
                      variant="soft"
                      onClick={() =>
                        window.open(recallData.media_shortcuts!.transcript!.download_url!, "_blank")
                      }
                    >
                      Download Transcript (Recall)
                    </Button>
                  )}
                </RecallDownloadLinks>
              )}

              <div style={{ marginTop: 16 }}>
                <Button
                  type="button"
                  variant="soft"
                  onClick={handleRefreshRecallData}
                  disabled={recallLoading}
                >
                  {recallLoading ? "Refreshing..." : "Refresh Recall Data"}
                </Button>
              </div>
            </>
          ) : (
            <div>No Recall data available.</div>
          )}
        </CollapsibleContent>
      </CollapsibleSection>

      {/* Debug Section */}
      <CollapsibleSection>
        <CollapsibleHeader isOpen={debugOpen} onClick={() => setDebugOpen(!debugOpen)}>
          <span>Debug Information</span>
          <span>{debugOpen ? "▼" : "▶"}</span>
        </CollapsibleHeader>
        <CollapsibleContent isOpen={debugOpen}>
          <div style={{ marginBottom: 16 }}>
            <StatusLabel style={{ marginBottom: 8, display: "block" }}>value_json</StatusLabel>
            <JsonBlock>
              <Lowlight
                language="json"
                value={JSON.stringify(recording.value_json, null, 2)}
                markers={[]}
              />
            </JsonBlock>
          </div>
          <div>
            <StatusLabel style={{ marginBottom: 8, display: "block" }}>Full Response</StatusLabel>
            <JsonBlock>
              <Lowlight language="json" value={JSON.stringify(recording, null, 2)} markers={[]} />
            </JsonBlock>
          </div>
        </CollapsibleContent>
      </CollapsibleSection>
    </Container>
  );
};
